<script setup>
import { ref } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { DxDataGrid } from 'devextreme-vue/data-grid'

import { useGridRef, useRouteColumnFilter, useODataSource, useLookup, useDefaultGridOptions, refreshGridAsync, getCreatedRowId, refreshChangedRowAsync } from '@/composables/data-grid'
import { useEntityChanges } from '@/composables/entity-changes'
import * as dataGridColumn from '@/composables/data-grid-column'
import { useArchive } from '@/composables/archive'
import { useChips } from '@/composables/chips'
import { useRoiCompareBasket } from '@/composables/roi-comparison'

import { planningApi } from '@/apis/planning.api'
import { usePortalStore, entityChangeTypeEnum } from '@/stores/portal.store'
import { usePlanningStore } from '@/stores/planning.store'

import ArchiveGridButton from '@/components/shared/ArchiveGridButton.vue'
import ArchivedGridToggle from '@/components/shared/ArchivedGridToggle.vue'
import ArchiveWarningDialog from '@/components/shared/ArchiveWarningDialog.vue'
import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import TagCellEditTemplate from '@/components/shared/TagCellEditTemplate.vue'
import QrCodeSizeDialog from '@/components/shared/QrCodeSizeDialog.vue'
import FileUrlEdit from '@/components/shared/FileUrlEdit.vue'
import FileViewer from '@/components/shared/FileViewer.vue'
import EquipmentEdit from '@/components/shared/EquipmentEdit.vue'
import GroupLinksTemplate from '@/components/shared/GroupLinksTemplate.vue'
import ServiceImport from '@/components/shared/ServiceImport.vue'
import RoiComparisonBasket from '@/components/roi/RoiComparisonBasket.vue'

import EquipmentsGridDetail from './EquipmentsGridDetail.vue'

const gridId = 'equipments-grid'

const i18n = getI18n()
const portalStore = usePortalStore()
const planningStore = usePlanningStore()
const editItem = ref(null)
const quickCreate = ref(false)
const onAddClick = (quick = false) => { quickCreate.value = quick === true; editItem.value = {} }
const showImport = ref(false)

planningStore.fetchGroupsAsync({ all: true }, true)

let localizedServiceTags = planningStore.getLocalizedServiceTags()
const getPrimaryServiceTags = () => localizedServiceTags
const localizedServiceAvailabilityTypes = portalStore.getLocalizedServiceAvailabilityTypes()
const printQrCodeToolbarButton = ref(null)
const selectedRows = ref([])
const showQrSizeDialog = ref(false)
const editors = {}

const { gridRef } = useGridRef()
const basket = useRoiCompareBasket(planningStore.roiComparisonTypeEnum.EQUIPMENT, gridRef)
const { onContentReady } = useRouteColumnFilter(gridRef)
const { getServiceTagChips, getServiceStructureTypeChip, rentedChip, needQrScanChip, isVehicleChip } = useChips()
const servicePresentationExport = {
  label: i18n.t('common.equipment', 2),
  onClick: (selectedRowsData) => planningStore.exportServicesExcelAsync(portalStore.serviceTypeEnum.EQUIPMENT, selectedRowsData.map(row => row.id)),
}

const gridDataSource = useODataSource(planningApi.getAbsoluteUrl() + planningApi.equipmentsUrl, {
  customParamsFn: () => showArchived.value ? { show_archived: true } : {},
  filter: [['structure_type', '=', 1], 'or', ['structure_type', '=', 2]],
})
const {
  archiveWarning, archiveButton, showArchived, archiveDisplayExpr, archiveCellTemplate, onRowPrepared: onArchiveRowPrepared, onRowRemoving,
  deleteSelectedToolbarItem, onDeleteSelectionChanged, onArchiveWarningConfirm,
} = useArchive({
  onArchive: async(id) => { await planningStore.archiveEquipmentAsync(id, { skipErrorMessage: true }) },
  onDelete: async(id, config) => { await planningStore.deleteEquipmentAsync(id, config) },
  onBulkDelete: async(data, config) => { await planningStore.bulkDeleteServicesAsync(portalStore.serviceTypeEnum.EQUIPMENT, data, config) },
  gridRef: gridRef,
})

let rentalCompanies = planningStore.companies.filter(i => i.is_rental)

const columns = [
  {
    type: 'buttons',
    showInColumnChooser: false,
    buttons: [
      { 
        name: 'edit', 
        visible: (e) => !e.row?.data?.archived,  
        onClick: (e) => { editItem.value = { ...e.row.data } }, 
      },
      { name: 'delete', visible: (e) => !e.row?.data?.archived },
      {
        name: 'clone',
        icon: 'copy',
        visible: (e) => !e.row?.data?.archived,
        onClick: async(e) => {
          await planningStore.cloneEquipmentAsync(e.row.data.id)
          await gridRef.value.instance.refresh()
        },
      },
      archiveButton,
    ],
    fixed: false,
    visibleIndex: -1,
    width: '8rem',
  },
  dataGridColumn.useAvatarColumn(),
  { dataField: 'code', caption: i18n.t('common.code'), width: '6rem', sortOrder: 'asc' },
  { dataField: 'name', caption: i18n.t('common.name'), validationRules: [{ type: 'required' }], width: '12rem' },
  { dataField: 'serial_number', caption: i18n.t('common.serialNumber'), width: '10rem' },
  { dataField: 'internal_serial', caption: i18n.t('common.internalSerial'), width: '10rem', visible: false },
  dataGridColumn.useStructureTypeColumn({
    dataSource: portalStore.getLocalizedServiceStructureTypes().filter(t =>
      [portalStore.serviceStructureTypeEnum.PHYSICAL, portalStore.serviceStructureTypeEnum.GROUP_PHYSICAL].includes(t.id)
    ),
  }),
  { dataField: 'description', caption: i18n.t('common.description'), width: '12rem' },
  dataGridColumn.useWarehouseColumn({
    dataField: 'warehouse',
    caption: i18n.t('common.warehouse'),
    allowEditing: false,
  }),
  dataGridColumn.useServiceTagsColumn({
    dataSource: getPrimaryServiceTags(),
    getTagsFn: getPrimaryServiceTags,
    cellTemplate: 'primary-service-tags-cell-template',
    editCellTemplate: 'primary-service-tags-cell-edit-template',
  }),
  dataGridColumn.useGroupsColumn(),
  {
    dataField: 'availability_type',
    caption: i18n.t('common.availabilityType'),
    lookup: useLookup({
      dataSource: localizedServiceAvailabilityTypes,
      valueExpr: 'id',
      displayExpr: 'name',
    }),
    cellTemplate: 'availability-type-cell-template',
    validationRules: [{ type: 'required' }]
  },
  {
    dataField: 'condition_value',
    caption: i18n.t('common.condition'),
    lookup: useLookup({
      dataSource: planningStore.getLocalizedEquipmentConditions(),
      valueExpr: 'id',
      displayExpr: 'name',
    }),
    cellTemplate: 'condition-cell-template',
  },
  {
    dataField: 'condition_comment',
    caption: i18n.t('common.conditionComment'),
  },
  {
    dataField: 'is_rented',
    dataType: 'boolean',
    caption: i18n.t('common.rented'),
    cellTemplate: 'rented-cell-template',
  },
  dataGridColumn.useCostColumn(),
  dataGridColumn.useCurrencyColumn({ editors: editors, referenceField: 'cost' }),
  {
    dataField: 'buy_date',
    caption: i18n.t('common.buyDate'),
    dataType: 'date',
  },
  { dataField: 'need_qr_scan', caption: i18n.t('common.needQrScan'), dataType: 'boolean', cellTemplate: 'need-qr-scan-cell-template' },
  { dataField: 'is_vehicle', caption: i18n.t('common.isVehicle'), dataType: 'boolean', cellTemplate: 'is-vehicle-cell-template' },
  { dataField: 'uuid', caption: i18n.t('common.uuid'), visible: false, formItem: { visible: false } },
  {
    dataField: 'service_files',
    caption: i18n.t('common.attachment', 2),
    cellTemplate: 'fileViewer',
    editCellTemplate: 'fileUploader',
    width: '16rem',
    allowExporting: false,
  },
  {
    dataField: 'rental_company_name', caption: i18n.t('common.rentalCompany.name'),
    cellTemplate: (element, rowData) => archiveCellTemplate(element, rowData, rowData.data.rental_company, 'name'),
  },
  {
    dataField: 'rental_company.id',
    lookup: useLookup({
      dataSource: () => rentalCompanies,
      valueExpr: 'id',
      displayExpr: (i) => archiveDisplayExpr(i, 'name'),
    }, {

      acceptCustomValue: true,
    }),
    setCellValue: (newData, value) => {
      const currentCompany = rentalCompanies.find(i => i.id === value)
      if (currentCompany) {
        newData.rental_company = {
          id: currentCompany?.id,
          name: currentCompany?.name,
          contact: currentCompany?.contact,
        }
      } else if (value === null) {
        newData.rental_company = null
      }
      if (value) {
        newData.availability_type = portalStore.serviceAvailabilityTypeEnum.USUALLY_UNAVAILABLE
        newData.buy_date = null
      }

      editors['availability_type'].option('readOnly', !!value)
      editors['buy_date'].option('readOnly', !!value)
      editors['rental_company.contact'].option('readOnly', !value)
    },
    caption: i18n.t('common.rentalCompany.name'),
    visible: false,
  },
  {
    dataField: 'rental_company.contact', caption: i18n.t('common.rentalCompany.contact'),
    cellTemplate: (element, rowData) => archiveCellTemplate(element, rowData, rowData.data.rental_company, 'contact'),
  },
  ...dataGridColumn.useRateCardColumns(),
  dataGridColumn.useArchivedAtColumn(),
  dataGridColumn.useArchivedColumn(),
]

const onSaved = async() => {
  await planningStore.fetchServiceTagsAsync({ all: true }, true)
  localizedServiceTags = planningStore.getLocalizedServiceTags()
  rentalCompanies = (await planningStore.fetchCompaniesAsync({all: true}, true))?.filter(i => i.is_rental) || []
  await gridRef.value.instance.refresh()
}

const onRowUpdating = async(e) => {
  if (!e.newData.service_files) {
    e.newData.service_files = e.oldData.service_files || []
  }
}

const onDownloadServiceFileAsync = async(e) => {
  if (e) {
    await planningStore.downloadServiceFileAsync(e.id, e.name)
  }
}

const onSelectionChanged = (e) => {
  selectedRows.value = e.selectedRowsData
  onDeleteSelectionChanged(e)
  basket.onSelectionChanged(e)
  printQrCodeToolbarButton.value.option('disabled', e.selectedRowsData.length === 0)
}

const onPrintQrCodeClick = async() => {
  if (selectedRows.value.length === 0) return
  showQrSizeDialog.value = true
}

const onQrSizeConfirm = async(size) => {
  await planningApi.downloadEquipmentQrCodesAsync(`${i18n.t('common.equipment', 2)}.pdf`, { ids: selectedRows.value.map(row => row.id), size })
  selectedRows.value = []
  gridRef.value.instance.clearSelection()
  await gridRef.value.instance.refresh()
  showQrSizeDialog.value = false
}

const onQrSizeCancel = () => {
  showQrSizeDialog.value = false
}

const printQrCodeWidget = () => ({
  widget: 'dxButton',
  location: 'after',
  options: {
    icon: 'mdi mdi-qrcode',
    disabled: true,
    text: i18n.t('common.printQrCode', 2),
    onClick: onPrintQrCodeClick,
    onInitialized: (e) => { printQrCodeToolbarButton.value = e.component }
  }
})

const gridOptions = useDefaultGridOptions({
  id: gridId,
  columns: columns,
  dataSource: gridDataSource,
  masterDetail: {
    enabled: true,
    template: 'master-detail',
  },
  editing: {
    mode: 'popup',
    allowUpdating: true,
    allowAdding: true,
    allowDeleting: true,
    useIcons: true,
  },
  onSaved: onSaved,
  onSelectionChanged: onSelectionChanged,
  exportWidgets: [printQrCodeWidget()],
  dataActionWidgets: [deleteSelectedToolbarItem],
  extraToolbarItems: [basket.toolbarItem],
  onImport: () => { showImport.value = true },
  onRowPrepared: (e) => { onArchiveRowPrepared(e); basket.onRowPrepared(e) },
  onRowRemoving: onRowRemoving,
  onRowUpdating: onRowUpdating,
  onContentReady: onContentReady,
  exportFileName: i18n.t('common.equipment', 2),
  presentationExports: [servicePresentationExport],
  selection: { mode: 'multiple', showCheckBoxesMode: 'onClick', selectAllMode: 'page' },
  clearFilterButton: true,
  archivedToggle: true,
  addButtons: [{
    icon: 'mdi-flash',
    hint: i18n.t('common.quickCreate'),
    onClick: () => onAddClick(true),
  }],
  addButton: { onClick: () => onAddClick(false) },
})

const onImported = async() => {
  showImport.value = false
  await onSaved()
}

const updateGrid = async (saved) => {
  const createdId = getCreatedRowId(editItem.value, saved)
  await planningStore.fetchServiceTagsAsync({ all: true }, true)
  localizedServiceTags = planningStore.getLocalizedServiceTags()
  editItem.value = null
  await refreshGridAsync(gridRef, createdId)
}


useEntityChanges([entityChangeTypeEnum.EQUIPMENT], (change) => refreshChangedRowAsync(gridRef, change), () => editItem.value !== null)
</script>
<template>
  <div>
    <q-dialog :model-value="editItem !== null" class="dialog-sm" @hide="updateGrid">
      <equipment-edit
        v-if="editItem !== null"
        :model-value="editItem"
        :initial-quick-create="quickCreate"
        @update="updateGrid"
        @cancel="updateGrid"
        @delete="updateGrid"
      />
    </q-dialog>
    <q-dialog v-model="showImport" persistent class="dialog-lg">
      <service-import
        v-if="showImport"
        :service-type="portalStore.serviceTypeEnum.EQUIPMENT"
        @update="onImported"
        @cancel="showImport = false"
      />
    </q-dialog>
    <archive-warning-dialog
      v-model="archiveWarning"
      @confirm="onArchiveWarningConfirm"
    />
    <qr-code-size-dialog
      v-model="showQrSizeDialog"
      :default-size="3"
      @confirm="onQrSizeConfirm"
      @cancel="onQrSizeCancel"
    />
    <roi-comparison-basket :basket="basket" />
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
    >
      <template #master-detail="{ data: templateData }">
        <equipments-grid-detail :equipment="templateData.data" />
      </template>
      <template #rented-cell-template="{ data: templateOptions }">
        <chip-template v-if="templateOptions.data.is_rented" :values="[rentedChip]" />
      </template>
      <template #need-qr-scan-cell-template="{ data: templateOptions }">
        <chip-template v-if="templateOptions.data.need_qr_scan" :values="[needQrScanChip]" />
      </template>
      <template #is-vehicle-cell-template="{ data: templateOptions }">
        <chip-template v-if="templateOptions.data.is_vehicle" :values="[isVehicleChip]" />
      </template>
      <template #groups-cell-template="{ data: templateOptions }">
        <group-links-template :group-ids="templateOptions.data.groups" />
      </template>
      <template #availability-type-cell-template="{ data: templateOptions }">
        <chip-template :values="localizedServiceAvailabilityTypes.filter(t => t.id == templateOptions.data.availability_type)" />
      </template>
      <template #structure-type-cell-template="{ data: templateOptions }">
        <chip-template :values="getServiceStructureTypeChip(templateOptions.data.structure_type)" />
      </template>
      <template #condition-cell-template="{ data: templateOptions }">
        <chip-template 
          v-if="templateOptions.data.condition_value != null"
          :values="planningStore.getLocalizedEquipmentConditions().filter(t => t.id == templateOptions.data.condition_value)"
        />
      </template>
      <template #archive-button="{ data: row }">
        <archive-grid-button :row="row" />
      </template>
      <template #archived-toggle>
        <archived-grid-toggle
          :show-archived="showArchived"
          :grid-ref="gridRef"
          @update:show-archived="showArchived = $event"
        />
      </template>
      <template #fileUploader="{ data: templateOptions }">
        <file-url-edit
          :cell-info="templateOptions"
          @download="onDownloadServiceFileAsync"
        />
      </template>
      <template #fileViewer="{ data: templateOptions }">
        <file-viewer
          dense
          :cell-info="templateOptions"
        />
      </template>
      <template #primary-service-tags-cell-template="{ data: templateOptions }">
        <chip-template
          v-if="templateOptions.data.tags && templateOptions.data.tags.length"
          :values="getServiceTagChips(templateOptions.data.tags)"
        />
      </template>
      <template #primary-service-tags-cell-edit-template="{ data: templateOptions }">
        <tag-cell-edit-template
          :value="templateOptions.data.tags"
          :data-source="getPrimaryServiceTags()"
          @value-changed="(e) => templateOptions.setValue(e.value)"
        />
      </template>
    </dx-data-grid>
  </div>
</template>